import { ApiError } from '../api/client'
import { useCallback, useEffect, useState } from 'react'
import { treasuryApi, type OutlookGroupBy, type TreasuryOutlookSeries } from '../api/treasury'
import { BarChart, ButtonGroup, Callout, Card, CardContent, CardHeader, ChartCard, DataTable } from './ui'

/**
 * F-164 / TASK-0418 — la serie del saldo prospettico: periodicità e orizzonte scelte,
 * grafico SVG in linea con barre entrate e uscite e linea del saldo, tabella di dettaglio.
 *
 * Una proiezione deterministica, non una previsione statistica. Tutti i calcoli stanno sul server: qui si mostrano.
 *
 * Componenti M3 (Card, ButtonGroup, DataTable plain); SVG in linea con i colori presi dai ruoli M3 (custom property). Si monta dietro il flag `finanza.serie_saldo`.
 */

const REASON: Record<string, string> = {
  disabled: "L'integrazione con conti non è attiva.",
  not_configured: "L'integrazione con conti non è configurata.",
  timeout: 'Conti non ha risposto in tempo.',
  unreachable: 'Conti non è raggiungibile.',
  unauthorized: 'Conti ha rifiutato le credenziali del CRM.',
  invalid_response: 'Conti ha risposto con dati non leggibili.',
  error: 'Conti ha risposto con un errore.',
}

const GROUPBY_LABELS: Record<OutlookGroupBy, string> = {
  day: 'Giorno',
  week: 'Settimana',
  month: 'Mese',
}

const HORIZONS = [30, 90, 180]

const GROUPBY_OPTIONS = (Object.keys(GROUPBY_LABELS) as OutlookGroupBy[]).map(g => ({ value: g, label: GROUPBY_LABELS[g] }))
const HORIZON_OPTIONS = HORIZONS.map(d => ({ value: String(d), label: `${d} giorni` }))

function eur(n: number | null | undefined): string {
  return n == null ? '—' : '€ ' + n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatPeriodLabel(start: string, end: string, groupBy: OutlookGroupBy): string {
  const startDate = new Date(start + 'T00:00:00Z')
  const endDate = new Date(end + 'T00:00:00Z')
  const opts: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'numeric', day: 'numeric' }
  if (groupBy === 'month') {
    return startDate.toLocaleDateString('it-IT', { year: 'numeric', month: 'long' })
  }
  return `${startDate.toLocaleDateString('it-IT', opts)} - ${endDate.toLocaleDateString('it-IT', opts)}`
}

interface ChartData {
  periods: Array<{
    label: string
    inflow: number
    outflow: number
    balance: number | null
  }>
}

function prepareChartData(series: TreasuryOutlookSeries): ChartData {
  const periods = series.periods.map(p => ({
    label: formatPeriodLabel(p.start, p.end, series.groupBy),
    inflow: p.expectedIn + p.certainIn,
    outflow: p.expectedOut + p.recurringOut + p.taxCommitment,
    balance: p.balance,
  }))

  return { periods }
}

export function TreasuryOutlookChart() {
  const [groupBy, setGroupBy] = useState<OutlookGroupBy>('week')
  const [days, setDays] = useState(90)
  const [data, setData] = useState<TreasuryOutlookSeries | null>(null)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    setError('')
    treasuryApi
      .outlookSeries(groupBy, days)
      .then(setData)
      .catch(e => {
        if (e instanceof ApiError && e.status === 403) {
          setData(null)
          setError('')
        } else {
          setData(null)
          setError(e instanceof Error ? e.message : 'Errore durante il caricamento della serie')
        }
      })
  }, [groupBy, days])

  useEffect(() => {
    load()
  }, [load])

  if (error) {
    return (
      <Card variant="outlined">
        <CardHeader title="Serie del saldo" />
        <CardContent>
          <Callout tone="error">{error}</Callout>
        </CardContent>
      </Card>
    )
  }

  if (!data) {
    return null
  }

  const chartData = prepareChartData(data)
  const labels = chartData.periods.map(p => p.label.split(' - ')[0] ?? p.label)

  return (
    <ChartCard
      title="Serie del saldo"
      headingLevel={2}
      subtitle="Entrate e uscite per periodo, con il saldo prospettico"
      legend={[{ label: 'Entrate', color: 'primary' }, { label: 'Uscite', color: 'error' }, { label: 'Saldo prospettico', color: 'neutral' }]}
      actions={
        <div className="md-cluster">
          <ButtonGroup aria-label="Periodo" size="sm" options={GROUPBY_OPTIONS} value={groupBy} onChange={v => setGroupBy(v as OutlookGroupBy)} />
          <ButtonGroup aria-label="Orizzonte" size="sm" options={HORIZON_OPTIONS} value={String(days)} onChange={v => setDays(Number(v))} />
        </div>
      }
    >
      <div className="md-stack">
          {!data.available && (
            <Callout tone="warning">
              {REASON[data.reason ?? ''] ?? data.message ?? 'Conti non è disponibile.'} Le barre del solo CRM sono stimate.
            </Callout>
          )}

          <BarChart
            label="Entrate, uscite e saldo prospettico per periodo"
            unit="eur"
            categories={labels}
            categoryTitles={chartData.periods.map(p => p.label)}
            series={[
              { id: 'in', name: 'Entrate', color: 'primary', values: chartData.periods.map(p => p.inflow) },
              { id: 'out', name: 'Uscite', color: 'error', values: chartData.periods.map(p => p.outflow) },
            ]}
            lines={[{ id: 'balance', name: 'Saldo prospettico', color: 'neutral', values: chartData.periods.map(p => p.balance) }]}
            emptyTitle="Nessun periodo"
            emptyDescription="La serie non contiene periodi nell'orizzonte scelto."
          />

          {chartData.periods.length > 0 && (
            <DataTable variant="plain" aria-label="Dettaglio della serie del saldo">
              <thead>
                <tr>
                  <th>Periodo</th>
                  <th className="md-num">Entrate certe</th>
                  <th className="md-num">Entrate attese</th>
                  <th className="md-num">Uscite</th>
                  <th className="md-num">Ricorrenze</th>
                  <th className="md-num">Imposte</th>
                  <th className="md-num">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {data.periods.map((p, i) => (
                  <tr key={i}>
                    <td>{chartData.periods[i]?.label}</td>
                    <td className="md-num">{eur(p.certainIn)}</td>
                    <td className="md-num">{eur(p.expectedIn)}</td>
                    <td className="md-num">{eur(p.expectedOut)}</td>
                    <td className="md-num">{eur(p.recurringOut)}</td>
                    <td className="md-num">{eur(p.taxCommitment)}</td>
                    <td className="md-num md-typescale-emphasized-body-medium">{eur(p.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          )}

          <p className="md-typescale-body-small text-muted">Proiezione deterministica, non una previsione statistica.</p>
      </div>
    </ChartCard>
  )
}
